import { IndexOrderTextData, TextData, TextWithTextBlockData } from '../../dto/EntityRepository';
import { CreateTextReq, DeleteTextReq, MetadataForQuery, UpdateReorderListReq, UpdateTextBulkReq, UpdateTextReq } from '../../dto/ReqDtoRepository';
import {
  DeleteResponse,
  TextManualOrderListResponse,
  TextResponse,
  TextSimplePageResponse,
  TextPageResponse,
  TextWithTextBlockAndRelatedProjectResponse,
  TextListResponse,
  TextWithTextBlockResponse,
} from '../../dto/ResDtoRepository';
import { useTempAlertStore } from '../../store/portal/tempAlertStore';
import { useApiWrapper } from './apiWrapper';
import { useModalPageStore, usePageStore } from '../../store/pageStore';
import { usePageOwnerIdentityStore } from '../../store/pageOwnerIdentityStore';
import { useTextListStore } from '../../store/TextListStore';
import {
  getText,
  getTextForOwner,
  getTextList,
  getTextListForOwner,
  getManualOrderTextListForOwner,
  getSimpleTextListForOwner,
  createText,
  updateText,
  updateTextBulk,
  deleteText,
  deleteTextsBulk,
  toggleTextAccess,
  reorderTexts as reorderTextsApi,
} from '../../api/textListApi';
import { useTextViewStore } from '../../store/TextViewStore';
import { useImportStationTextStore } from '../../store/importStationTextStore';
import { useTextIndexStore } from '../../store/TextIndexStore';
import { runCurrentArchiveRequest } from '../../util/currentArchiveRequest';
import { useEditModeStore } from '../../store/authStore';
import { useTextTagStore } from '../../store/textTagStore';
import { toTextListFilterData } from '../../util/textTag';
import type { ResourceScope, ResourceStatus } from '../../util/scopedResource';


interface UseTextListStateResult {
  textList: TextData[];
  listOwnerAui: string | null;
  listScope: ResourceScope | null;
  status: ResourceStatus;
}

interface UseTextListActionsResult {
  getText: (textId: string) => Promise<void>;
  getTextOwner: (textId: string) => Promise<void>;
  getTextList: (metaData: MetadataForQuery) => Promise<void>;
  getTextListOwner: (metaData: MetadataForQuery) => Promise<void>;
  /** 수동 순서(lex index)가 있는 텍스트만 — 인덱싱 패널 초기 목록 */
  getManualOrderTextListForOwner: () => Promise<void>;
  getSimpleTextList: (metaData: MetadataForQuery) => Promise<void>;
  updateText: (data: UpdateTextReq) => Promise<void>;
  updateTextBulk: (data: UpdateTextBulkReq) => Promise<void>;
  reorderTexts: (data: UpdateReorderListReq) => Promise<void>;
  toggleTextAccess: (data: UpdateTextReq) => Promise<void>;
  createText: (data: CreateTextReq) => Promise<void>;
  deleteText: (data: DeleteTextReq) => Promise<void>;
  deleteTextsBulk: (data: string[]) => Promise<void>;
  clearActiveText: () => void;
}

type UseTextListResult = UseTextListStateResult & UseTextListActionsResult;

export const useTextListState = (): UseTextListStateResult => {
  const textList = useTextListStore((s) => s.textList);
  const listOwnerAui = useTextListStore((s) => s.listOwnerAui);
  const listScope = useTextListStore((s) => s.listScope);
  const status = useTextListStore((s) => s.status);

  return { textList, listOwnerAui, listScope, status };
};

export const useTextListActions = (): UseTextListActionsResult => {
  const pageOwnerAui = usePageOwnerIdentityStore((s) => s.pageOwner?.aui);
  const setTextList = useTextListStore((s) => s.setTextList);
  const beginTextListFetch = useTextListStore((s) => s.beginTextListFetch);
  const setTextListWithScope = useTextListStore((s) => s.setTextListWithScope);
  const setTextListError = useTextListStore((s) => s.setTextListError);
  const addText = useTextListStore((s) => s.addText);
  const updateAfterIsPublicToggle = useTextListStore((s) => s.updateAfterIsPublicToggle);
  const setSimpleList = useImportStationTextStore((s) => s.setSimpleList);
  const setActiveText = useTextViewStore((s) => s.setActiveText);
  const setOnlyAccessValue = useTextViewStore((s) => s.setOnlyAccessValue);
  const setActiveTextBlockList = useTextViewStore((s) => s.setActiveTextBlockList);
  const afterDeleteActiveText = useTextViewStore((s) => s.afterDeleteActiveText);
  const clearActiveText = useTextViewStore((s) => s.clearActiveText);
  const setActiveRelatedProjectList = useTextViewStore((s) => s.setActiveRelatedProjectList);
  const setUpdatedTempAlert = useTempAlertStore((s) => s.setUpdatedTempAlert);
  const setDeletedTempAlert = useTempAlertStore((s) => s.setDeletedTempAlert);
  const clearSelectedIndexingTextList = useTextIndexStore((s) => s.clearSelectedIndexingTextList);
  const setPage = usePageStore((s) => s.setPage);
  const setModalPage = useModalPageStore((s) => s.setModalPage);
  const withApiHandler = useApiWrapper();

  const handleGetTextSuccess = (response: TextWithTextBlockAndRelatedProjectResponse) => {
    const data = response.data;
    const text = data as TextWithTextBlockData;
    const { textBlockList, ...textWithoutBlockList } = text;
    setActiveText(textWithoutBlockList);
    setActiveTextBlockList(textBlockList);
    setActiveRelatedProjectList(data.relatedProjectList);
  };

  const handleGetSimpleTextPageSuccess = (response: TextSimplePageResponse) => {
    const data = response.data;
    setModalPage(data);
    setSimpleList(data.content);
  };

  const handleGetTextPageSuccess = (
    response: TextPageResponse,
    ownerAui: string,
    scope: ResourceScope,
  ) => {
    const data = response.data;
    setPage(data);
    setTextListWithScope(data.content, ownerAui, scope);
  };

  const fetchTextList = async (
    aui: string,
    scope: ResourceScope,
    metaData: MetadataForQuery,
  ) => {
    beginTextListFetch(aui, scope);
    try {
      return await runCurrentArchiveRequest(
        aui,
        () => (scope === 'owner' ? getTextListForOwner(aui, metaData) : getTextList(aui, metaData)),
        (response) => handleGetTextPageSuccess(response, aui, scope),
      );
    } catch (error) {
      setTextListError();
      throw error;
    }
  };

  const handleUpdateTextSuccess = (response: TextWithTextBlockResponse) => {
    const data = response.data;
    const text = data as TextWithTextBlockData;
    const { textBlockList, ...textWithoutBlockList } = text;
    const { textList } = useTextListStore.getState();
    const newTextList = textList.map((each) => each.id === textWithoutBlockList.id ? textWithoutBlockList : each);
    setUpdatedTempAlert();
    setActiveText(textWithoutBlockList);
    setActiveTextBlockList(textBlockList);
    setTextList(newTextList);
  };

  const handleUpdateTextBulkSuccess = (response: TextListResponse) => {
    const updated = response.data;
    const byId = new Map(updated.map((t) => [t.id, t]));
    const prev = useTextListStore.getState().textList;
    setTextList(prev.map((t) => byId.get(t.id) ?? t));
    if (useTextViewStore.getState().multiSelectedText.length > 0) {
      useTextViewStore.getState().addMultiSelectedText(updated);
    }
    useTextViewStore.getState().setMultiHasChanged(false);
    setUpdatedTempAlert();
  };

  const handleToggleTextAccessSuccess = (response: TextResponse) => {
    const data = response.data;
    setUpdatedTempAlert();
    setOnlyAccessValue(data.isPublic, data.tags);
    updateAfterIsPublicToggle(data.id, data.isPublic);
  };

  const handleDeleteTextSuccess = (_response: DeleteResponse) => {
    console.log("deleted well");
    afterDeleteActiveText();
    useEditModeStore.getState().setIsEditMode(false);
    setDeletedTempAlert();
  };

  const handleCreatTextSuccess = (response: TextWithTextBlockResponse) => {
    const data = response.data;
    const text = data as TextWithTextBlockData;
    const { textBlockList, ...textWithoutBlockList } = text;
    const { pageSize } = useTextListStore.getState();
    addText(data, pageSize);
    setActiveText(textWithoutBlockList);
    setActiveTextBlockList(textBlockList);
  };

  const handleReorderTextsSuccess = (_response: TextListResponse) => {
    setUpdatedTempAlert();
    clearSelectedIndexingTextList();
  };

  const handleGetManualOrderTextListSuccess = (response: TextManualOrderListResponse) => {
    const rows: IndexOrderTextData[] = response.data.map((dto) => ({
      id: String(dto.id),
      title: dto.title,
      index: 0,
    }));
    useTextIndexStore.getState().replaceSelectedIndexingTextList(rows);
  };

  const reloadCurrentTextListPage = () => {
    const { page } = usePageStore.getState();
    const { pageSize, sortData } = useTextListStore.getState();
    const selectedTags = useTextTagStore.getState().selectedTags;
    void getTextListOwnerHandler({
      page: page.page,
      size: pageSize,
      sortData,
      filterData: toTextListFilterData(selectedTags),
    });
  };

  const handleTextRequest = async (
    action:
      | 'get'
      | 'get_for_owner'
      | 'get_page'
      | 'get_page_for_owner'
      | 'get_simple_page'
      | 'update'
      | 'update_bulk'
      | 'reorder'
      | 'toggle_access'
      | 'create'
      | 'delete'
      | 'delete_bulk'
      | 'get_manual_order_list_for_owner',
    data?: UpdateTextReq | CreateTextReq | DeleteTextReq | UpdateTextBulkReq | UpdateReorderListReq | string | string[],
    metaData?: MetadataForQuery
  ) => {
    if (!pageOwnerAui) return;
    const aui = pageOwnerAui;
    const apiFunction = async () => {
      switch (action) {
        case 'delete':
          await runCurrentArchiveRequest(
            aui,
            () => deleteText(aui, data as DeleteTextReq),
            (response) => {
              handleDeleteTextSuccess(response);
              reloadCurrentTextListPage();
            },
          );
          break;
        case 'delete_bulk':
          await runCurrentArchiveRequest(
            aui,
            () => deleteTextsBulk(aui, data as string[]),
            (response) => {
              handleDeleteTextSuccess(response);
              reloadCurrentTextListPage();
            },
          );
          break;
        case 'update':
          await runCurrentArchiveRequest(
            aui,
            () => updateText(aui, data as UpdateTextReq),
            handleUpdateTextSuccess,
          );
          break;
        case 'update_bulk':
          await runCurrentArchiveRequest(
            aui,
            () => updateTextBulk(aui, data as UpdateTextBulkReq),
            handleUpdateTextBulkSuccess,
          );
          break;
        case 'reorder':
          await runCurrentArchiveRequest(
            aui,
            () => reorderTextsApi(aui, data as UpdateReorderListReq),
            handleReorderTextsSuccess,
          );
          break;
        case 'toggle_access':
          await runCurrentArchiveRequest(
            aui,
            () => toggleTextAccess(aui, data as UpdateTextReq),
            handleToggleTextAccessSuccess,
          );
          break;
        case 'create':
          await runCurrentArchiveRequest(
            aui,
            () => createText(aui, data as CreateTextReq),
            handleCreatTextSuccess,
          );
          break;
        case 'get_simple_page':
          await runCurrentArchiveRequest(
            aui,
            () => getSimpleTextListForOwner(aui, metaData!),
            handleGetSimpleTextPageSuccess,
          );
          break;
        case 'get_page':
          await fetchTextList(aui, 'public', metaData!);
          break;
        case 'get_page_for_owner':
          await fetchTextList(aui, 'owner', metaData!);
          break;
        case 'get_manual_order_list_for_owner':
          await runCurrentArchiveRequest(
            aui,
            () => getManualOrderTextListForOwner(aui),
            handleGetManualOrderTextListSuccess,
          );
          break;
        case 'get_for_owner':
          await runCurrentArchiveRequest(
            aui,
            () => getTextForOwner(aui, data as string),
            handleGetTextSuccess,
          );
          break;
        case 'get':
        default:
          await runCurrentArchiveRequest(
            aui,
            () => getText(data as string),
            handleGetTextSuccess,
          );
          break;
      }
    };

    await withApiHandler(apiFunction, [aui, action, data]);
  };


  const getTextHandler = (textId: string) => handleTextRequest('get', textId);
  const getTextOwnerHandler = (textId: string) => handleTextRequest('get_for_owner', textId);
  const getTextListHandler = (metaData: MetadataForQuery) => handleTextRequest('get_page', undefined, metaData);
  const getTextListOwnerHandler = (metaData: MetadataForQuery) => handleTextRequest('get_page_for_owner', undefined, metaData);
  const getManualOrderTextListForOwnerHandler = () => handleTextRequest('get_manual_order_list_for_owner');
  const getSimpleTextListHandler = (metaData: MetadataForQuery) => handleTextRequest('get_simple_page', undefined, metaData);
  const createTextHandler = (data: CreateTextReq) => handleTextRequest('create', data);
  const updateTextHandler = (data: UpdateTextReq) => handleTextRequest('update', data);
  const updateTextBulkHandler = (data: UpdateTextBulkReq) => handleTextRequest('update_bulk', data);
  const reorderTextsHandler = (data: UpdateReorderListReq) => handleTextRequest('reorder', data);
  const toggleTextAccessHandler = (data: UpdateTextReq) => handleTextRequest('toggle_access', data);
  const deleteTextHandler = (data: DeleteTextReq) => handleTextRequest('delete', data);
  const deleteTextsBulkHandler = (data: string[]) => handleTextRequest('delete_bulk', data);

  return {
    getText: getTextHandler,
    getTextOwner: getTextOwnerHandler,
    getTextList: getTextListHandler,
    getTextListOwner: getTextListOwnerHandler,
    getManualOrderTextListForOwner: getManualOrderTextListForOwnerHandler,
    getSimpleTextList: getSimpleTextListHandler,
    createText: createTextHandler,
    updateText: updateTextHandler,
    updateTextBulk: updateTextBulkHandler,
    reorderTexts: reorderTextsHandler,
    toggleTextAccess: toggleTextAccessHandler,
    deleteText: deleteTextHandler,
    deleteTextsBulk: deleteTextsBulkHandler,
    clearActiveText,
  };
};

/** 기존 호출부 호환: state + actions */
export const useTextList = (): UseTextListResult => ({
  ...useTextListState(),
  ...useTextListActions(),
});
